/* eslint-disable max-lines */
import * as React from "react";

import { OldProgressBarQuestionnaireSectionUnconnected } from "@teamprevent/common/questionnaires/components/OldProgressBarQuestionnaireSection";
import { getQuestionConfig, CustomQuestionConfig } from "@teamprevent/common/questionnaires/questionnaireConfig";
import { getFeatureToggles } from "@teamprevent/common/getFeatureToggles";
import * as FeatureToggleNames from "@teamprevent/common/featureToggleNames";

import { connect } from "react-redux";
import { mapDispatchToQuestionnaireSectionProps, makeMapStateToQuestionnaireSectionProps } from "@teamprevent/common/questionnaires/mappers";
import { CustomQuestionsQuestionnaireSectionProps } from "@teamprevent/common/questionnaires/QuestionnaireSectionProps";
import { TextQuestion } from "@teamprevent/common/questionnaires/components/questions/TextQuestion";
import { NumberQuestion } from "@teamprevent/common/questionnaires/components/questions/NumberQuestion";
import { MultiButtonQuestion } from "@teamprevent/common/questionnaires/components/questions/MultiButtonQuestion";
import { getAnswerToQuestionWithId } from "@teamprevent/common/questionnaires/getAnswerToQuestionWithId";
import { checkExistenceOfQuestionWithId } from "@teamprevent/common/questionnaires/checkExistenceOfQuestionWithId";
import { PRESENT_ABSENT_OPTIONS, POSITIVE_NEGATIVE_OPTIONS, NORMAL_ABNORMAL_OPTIONS } from "@teamprevent/common/healthSurveillance/models";
import { values } from "lodash";

import { ImageAnnotatorQuestion } from "@teamprevent/common/questionnaires/components/questions/imageAnnotatorQuestion/ImageAnnotatorQuestion";
import { DropdownQuestion } from "@teamprevent/common/questionnaires/components/questions/DropdownQuestion";
import { YesNoWithFollowUpQuestion } from "@teamprevent/common/questionnaires/components/questions/YesNoWithFollowUpQuestion";

import { validMedicalValues } from "@teamprevent/common/validators/valid-medical-values";

import { rightGripStrengthAverageQuestion, leftGripStrengthAverageQuestion } from "../consultationQuestions/havsQuestions";
import { HealthSurveillanceHandsImageUrl } from "../models";

import * as questionIds from "../consultationQuestions/questionIds";
import { sections } from "../consultation";

const stockholmSensorineuralGradingOptions = [
    { value: "0sn", text: "0sn" },
    { value: "1sn", text: "1sn" },
    { value: "2sn", text: "2sn" },
    { value: "3sn", text: "3sn" },
];

const stockholmVascularGradingOptions = [
    { value: "0", text: "0" },
    { value: "1v", text: "1v" },
    { value: "2v", text: "2v" },
    { value: "3v", text: "3v" },
    { value: "4v", text: "4v" },
];

// eslint-disable-next-line max-len
const HealthSurveillanceConsultationExaminationSectionUnconnected: React.FunctionComponent<CustomQuestionsQuestionnaireSectionProps> = (props: CustomQuestionsQuestionnaireSectionProps) => {
    const healthSurveillanceCrBugsEnabled = getFeatureToggles()[FeatureToggleNames.HealthSurveillanceCrBugs];

    const questions = values(props.questions || {});
    const onUpdateRightGripStrength1 = (strength1: number) => onUpdateAverageGripStrength(
        strength1,
        getAnswerToQuestionWithId(questions, questionIds.RightGripStrength2) as number,
        getAnswerToQuestionWithId(questions, questionIds.RightGripStrength3) as number,
        rightGripStrengthAverageQuestion);
    const onUpdateRightGripStrength2 = (strength2: number) => onUpdateAverageGripStrength(
        getAnswerToQuestionWithId(questions, questionIds.RightGripStrength1) as number,
        strength2,
        getAnswerToQuestionWithId(questions, questionIds.RightGripStrength3) as number,
        rightGripStrengthAverageQuestion);
    const onUpdateRightGripStrength3 = (strength3: number) => onUpdateAverageGripStrength(
        getAnswerToQuestionWithId(questions, questionIds.RightGripStrength1) as number,
        getAnswerToQuestionWithId(questions, questionIds.RightGripStrength2) as number,
        strength3,
        rightGripStrengthAverageQuestion);
    const onUpdateLeftGripStrength1 = (strength1: number) => onUpdateAverageGripStrength(
        strength1,
        getAnswerToQuestionWithId(questions, questionIds.LeftGripStrength2) as number,
        getAnswerToQuestionWithId(questions, questionIds.LeftGripStrength3) as number,
        leftGripStrengthAverageQuestion);
    const onUpdateLeftGripStrength2 = (strength2: number) => onUpdateAverageGripStrength(
        getAnswerToQuestionWithId(questions, questionIds.LeftGripStrength1) as number,
        strength2,
        getAnswerToQuestionWithId(questions, questionIds.LeftGripStrength3) as number,
        leftGripStrengthAverageQuestion);
    const onUpdateLeftGripStrength3 = (strength3: number) => onUpdateAverageGripStrength(
        getAnswerToQuestionWithId(questions, questionIds.LeftGripStrength1) as number,
        getAnswerToQuestionWithId(questions, questionIds.LeftGripStrength2) as number,
        strength3,
        leftGripStrengthAverageQuestion);
    const onUpdateAverageGripStrength = (strength1: number, strength2: number, strength3: number, averageQuestion: CustomQuestionConfig) => {
        let answer;
        if ((strength1 || strength1 === 0) && (strength2 || strength2 === 0) && (strength3 || strength3 === 0)) {
            answer = ((strength1 + strength2 + strength3) / 3).toFixed(2);
        } else {
            answer = null;
        }
        props.updateAnswer({
            sectionId: sections.Examination.id,
            id: averageQuestion.id,
            questionnaireKey: props.questionnaireKey,
            question: {
                isValid: true,
                question: averageQuestion.questionText,
                answer
            }
        });
    };
    const isOldVersion = (props.readonly && checkExistenceOfQuestionWithId(questions, questionIds.HavsAssessmentRequired));

    return (
        <OldProgressBarQuestionnaireSectionUnconnected
            {...props}
            {...sections.Examination}
        >
            {YesNoWithFollowUpQuestion({
                readonly: props.readonly,
                questionConfigs: [
                    getQuestionConfig(props.questionConfigs, questionIds.ExaminationUnableToCarryOut),
                    getQuestionConfig(props.questionConfigs, questionIds.ExaminationUnableToCarryOutReason)
                ],
                mandatory: true,
                followUpMandatory: true,
                questions: props.questions
            })}
            {getAnswerToQuestionWithId(questions, questionIds.ExaminationUnableToCarryOut) !== MultiButtonQuestion.YesOption && <>
                <TextQuestion
                    readonly={props.readonly}
                    questionConfig={getQuestionConfig(props.questionConfigs, questionIds.LastExposureToVibration)}
                    mandatory={isOldVersion}
                />
                <NumberQuestion
                    readonly={props.readonly}
                    questionConfig={getQuestionConfig(props.questionConfigs, questionIds.RoomTemperature)}
                    mandatory={isOldVersion}
                />
                <ImageAnnotatorQuestion
                    imageHref={HealthSurveillanceHandsImageUrl}
                    readonly={props.readonly}
                    questionConfig={getQuestionConfig(props.questionConfigs, questionIds.AppearanceOfHands)}
                    allowNotes
                />
                <NumberQuestion
                    readonly={props.readonly}
                    questionConfig={getQuestionConfig(props.questionConfigs, questionIds.RightPulseRate)}
                    mandatory={isOldVersion}
                />
                <NumberQuestion
                    readonly={props.readonly}
                    questionConfig={getQuestionConfig(props.questionConfigs, questionIds.LeftPulseRate)}
                    mandatory={isOldVersion}
                />
                {healthSurveillanceCrBugsEnabled &&
                    <>
                        <TextQuestion
                            readonly={props.readonly}
                            questionConfig={getQuestionConfig(props.questionConfigs, questionIds.RightBloodPressure)}
                            mandatory={isOldVersion}
                            validators={[validMedicalValues]}
                        />
                        <TextQuestion
                            readonly={props.readonly}
                            questionConfig={getQuestionConfig(props.questionConfigs, questionIds.LeftBloodPressure)}
                            mandatory={isOldVersion}
                            validators={[validMedicalValues]}
                        />
                    </>
                }
                {!healthSurveillanceCrBugsEnabled &&
                    <>
                        <NumberQuestion
                            readonly={props.readonly}
                            questionConfig={getQuestionConfig(props.questionConfigs, questionIds.RightBloodPressure)}
                            mandatory={isOldVersion}
                        />
                        <NumberQuestion
                            readonly={props.readonly}
                            questionConfig={getQuestionConfig(props.questionConfigs, questionIds.LeftBloodPressure)}
                            mandatory={isOldVersion}
                        />
                    </>
                }
                {isOldVersion &&
                    <>
                        <TextQuestion
                            readonly={props.readonly}
                            questionConfig={getQuestionConfig(props.questionConfigs, questionIds.RightPositiveRadialPulse)}
                            mandatory
                        />
                        <TextQuestion
                            readonly={props.readonly}
                            questionConfig={getQuestionConfig(props.questionConfigs, questionIds.RightAbsentRadialPulse)}
                            mandatory
                        />
                        <TextQuestion
                            readonly={props.readonly}
                            questionConfig={getQuestionConfig(props.questionConfigs, questionIds.LeftPositiveRadialPulse)}
                            mandatory
                        />
                        <TextQuestion
                            readonly={props.readonly}
                            questionConfig={getQuestionConfig(props.questionConfigs, questionIds.LeftAbsentRadialPulse)}
                            mandatory
                        />
                        <TextQuestion
                            readonly={props.readonly}
                            questionConfig={getQuestionConfig(props.questionConfigs, questionIds.RightPositiveUlnarPulse)}
                            mandatory
                        />
                        <TextQuestion
                            readonly={props.readonly}
                            questionConfig={getQuestionConfig(props.questionConfigs, questionIds.RightAbsentUlnarPulse)}
                            mandatory
                        />
                        <TextQuestion
                            readonly={props.readonly}
                            questionConfig={getQuestionConfig(props.questionConfigs, questionIds.LeftPositiveUlnarPulse)}
                            mandatory
                        />
                        <TextQuestion
                            readonly={props.readonly}
                            questionConfig={getQuestionConfig(props.questionConfigs, questionIds.LeftAbsentUlnarPulse)}
                            mandatory
                        />
                        <TextQuestion
                            readonly={props.readonly}
                            questionConfig={getQuestionConfig(props.questionConfigs, questionIds.RightPositiveAllensTest)}
                            mandatory
                        />
                        <TextQuestion
                            readonly={props.readonly}
                            questionConfig={getQuestionConfig(props.questionConfigs, questionIds.RightNegativeAllensTest)}
                            mandatory
                        />
                        <TextQuestion
                            readonly={props.readonly}
                            questionConfig={getQuestionConfig(props.questionConfigs, questionIds.LeftPositiveAllensTest)}
                            mandatory
                        />
                        <TextQuestion
                            readonly={props.readonly}
                            questionConfig={getQuestionConfig(props.questionConfigs, questionIds.LeftNegativeAllensTest)}
                            mandatory
                        />
                        <TextQuestion
                            readonly={props.readonly}
                            questionConfig={getQuestionConfig(props.questionConfigs, questionIds.RightNormalSemmesWeinstein)}
                            mandatory
                        />
                        <TextQuestion
                            readonly={props.readonly}
                            questionConfig={getQuestionConfig(props.questionConfigs, questionIds.RightAbnormalSemmesWeinstein)}
                            mandatory
                        />
                        <TextQuestion
                            readonly={props.readonly}
                            questionConfig={getQuestionConfig(props.questionConfigs, questionIds.LeftNormalSemmesWeinstein)}
                            mandatory
                        />
                        <TextQuestion
                            readonly={props.readonly}
                            questionConfig={getQuestionConfig(props.questionConfigs, questionIds.LeftAbnormalSemmesWeinstein)}
                            mandatory
                        />
                        <TextQuestion
                            readonly={props.readonly}
                            questionConfig={getQuestionConfig(props.questionConfigs, questionIds.RightNormalPurduePegboardTest)}
                            mandatory
                        />
                        <TextQuestion
                            readonly={props.readonly}
                            questionConfig={getQuestionConfig(props.questionConfigs, questionIds.RightAbnormalPurduePegboardTest)}
                            mandatory
                        />
                        <TextQuestion
                            readonly={props.readonly}
                            questionConfig={getQuestionConfig(props.questionConfigs, questionIds.LeftNormalPurduePegboardTest)}
                            mandatory
                        />
                        <TextQuestion
                            readonly={props.readonly}
                            questionConfig={getQuestionConfig(props.questionConfigs, questionIds.LeftAbnormalPurduePegboardTest)}
                            mandatory
                        />
                        <p>Further tests, where appropriate</p>
                        <TextQuestion
                            readonly={props.readonly}
                            questionConfig={getQuestionConfig(props.questionConfigs, questionIds.RightNormalAdsonsTest)}
                            mandatory
                        />
                        <TextQuestion
                            readonly={props.readonly}
                            questionConfig={getQuestionConfig(props.questionConfigs, questionIds.RightAbnormalAdsonsTest)}
                            mandatory
                        />
                        <TextQuestion
                            readonly={props.readonly}
                            questionConfig={getQuestionConfig(props.questionConfigs, questionIds.LeftNormalAdsonsTest)}
                            mandatory
                        />
                        <TextQuestion
                            readonly={props.readonly}
                            questionConfig={getQuestionConfig(props.questionConfigs, questionIds.LeftAbnormalAdsonsTest)}
                            mandatory
                        />
                        <TextQuestion
                            readonly={props.readonly}
                            questionConfig={getQuestionConfig(props.questionConfigs, questionIds.RightNormalTinelsTest)}
                            mandatory
                        />
                        <TextQuestion
                            readonly={props.readonly}
                            questionConfig={getQuestionConfig(props.questionConfigs, questionIds.RightAbnormalTinelsTest)}
                            mandatory
                        />
                        <TextQuestion
                            readonly={props.readonly}
                            questionConfig={getQuestionConfig(props.questionConfigs, questionIds.LeftNormalTinelsTest)}
                            mandatory
                        />
                        <TextQuestion
                            readonly={props.readonly}
                            questionConfig={getQuestionConfig(props.questionConfigs, questionIds.LeftAbnormalTinelsTest)}
                            mandatory
                        />
                        <TextQuestion
                            readonly={props.readonly}
                            questionConfig={getQuestionConfig(props.questionConfigs, questionIds.RightNormalPhalensTest)}
                            mandatory
                        />
                        <TextQuestion
                            readonly={props.readonly}
                            questionConfig={getQuestionConfig(props.questionConfigs, questionIds.RightAbnormalPhalensTest)}
                            mandatory
                        />
                        <TextQuestion
                            readonly={props.readonly}
                            questionConfig={getQuestionConfig(props.questionConfigs, questionIds.LeftNormalPhalensTest)}
                            mandatory
                        />
                        <TextQuestion
                            readonly={props.readonly}
                            questionConfig={getQuestionConfig(props.questionConfigs, questionIds.LeftAbnormalPhalensTest)}
                            mandatory
                        />
                    </>
                }
                {!isOldVersion &&
                    <>
                        <p><b>Radial Pulse:</b></p>
                        <MultiButtonQuestion
                            readonly={props.readonly}
                            questionConfig={getQuestionConfig(props.questionConfigs, questionIds.RadialPulseRight)}
                            values={PRESENT_ABSENT_OPTIONS}
                            hideDivider
                        />
                        <MultiButtonQuestion
                            readonly={props.readonly}
                            questionConfig={getQuestionConfig(props.questionConfigs, questionIds.RadialPulseLeft)}
                            values={PRESENT_ABSENT_OPTIONS}
                        />
                        <p><b>Ulnar Pulse:</b></p>
                        <MultiButtonQuestion
                            readonly={props.readonly}
                            questionConfig={getQuestionConfig(props.questionConfigs, questionIds.UlnarPulseRight)}
                            values={PRESENT_ABSENT_OPTIONS}
                            hideDivider
                        />
                        <MultiButtonQuestion
                            readonly={props.readonly}
                            questionConfig={getQuestionConfig(props.questionConfigs, questionIds.UlnarPulseLeft)}
                            values={PRESENT_ABSENT_OPTIONS}
                        />
                        <p><b>Allen's Test:</b></p>
                        <MultiButtonQuestion
                            readonly={props.readonly}
                            questionConfig={getQuestionConfig(props.questionConfigs, questionIds.AllensTestRight)}
                            values={POSITIVE_NEGATIVE_OPTIONS}
                            hideDivider
                        />
                        <MultiButtonQuestion
                            readonly={props.readonly}
                            questionConfig={getQuestionConfig(props.questionConfigs, questionIds.AllensTestLeft)}
                            values={POSITIVE_NEGATIVE_OPTIONS}
                        />
                        <h3><p><b>Nervous System</b></p></h3>
                        <p><b>Semmes-Weinstein:</b></p>
                        <MultiButtonQuestion
                            readonly={props.readonly}
                            questionConfig={getQuestionConfig(props.questionConfigs, questionIds.SemmesWeinsteinRight)}
                            values={NORMAL_ABNORMAL_OPTIONS}
                            hideDivider
                        />
                        <TextQuestion
                            readonly={props.readonly}
                            questionConfig={getQuestionConfig(props.questionConfigs, questionIds.SemmesWeinsteinRightScore)}
                            hideDivider
                        />
                        <MultiButtonQuestion
                            readonly={props.readonly}
                            questionConfig={getQuestionConfig(props.questionConfigs, questionIds.SemmesWeinsteinLeft)}
                            values={NORMAL_ABNORMAL_OPTIONS}
                            hideDivider
                        />
                        <TextQuestion
                            readonly={props.readonly}
                            questionConfig={getQuestionConfig(props.questionConfigs, questionIds.SemmesWeinsteinLeftScore)}
                        />
                        <p><b>Manual Dexterity (Purdue Pegboard test):</b></p>
                        <MultiButtonQuestion
                            readonly={props.readonly}
                            questionConfig={getQuestionConfig(props.questionConfigs, questionIds.ManualDexterityRight)}
                            values={NORMAL_ABNORMAL_OPTIONS}
                            hideDivider
                        />
                        <TextQuestion
                            readonly={props.readonly}
                            questionConfig={getQuestionConfig(props.questionConfigs, questionIds.ManualDexterityRightScore)}
                            hideDivider
                        />
                        <MultiButtonQuestion
                            readonly={props.readonly}
                            questionConfig={getQuestionConfig(props.questionConfigs, questionIds.ManualDexterityLeft)}
                            values={NORMAL_ABNORMAL_OPTIONS}
                            hideDivider
                        />
                        <TextQuestion
                            readonly={props.readonly}
                            questionConfig={getQuestionConfig(props.questionConfigs, questionIds.ManualDexterityLeftScore)}
                            hideDivider
                        />
                        <p><b>Both</b></p>
                        <TextQuestion
                            readonly={props.readonly}
                            questionConfig={getQuestionConfig(props.questionConfigs, questionIds.ManualDexterityBothScore)}
                        />
                        <h3><p><b>Futher tests, where appropriate</b></p></h3>
                        <p><b>Adson's Test:</b></p>
                        <MultiButtonQuestion
                            readonly={props.readonly}
                            questionConfig={getQuestionConfig(props.questionConfigs, questionIds.AdsonsTestRight)}
                            values={NORMAL_ABNORMAL_OPTIONS}
                            hideDivider
                        />
                        <MultiButtonQuestion
                            readonly={props.readonly}
                            questionConfig={getQuestionConfig(props.questionConfigs, questionIds.AdsonsTestLeft)}
                            values={NORMAL_ABNORMAL_OPTIONS}
                        />
                        <p><b>Tinel's Test:</b></p>
                        <MultiButtonQuestion
                            readonly={props.readonly}
                            questionConfig={getQuestionConfig(props.questionConfigs, questionIds.TinelsTestRight)}
                            values={NORMAL_ABNORMAL_OPTIONS}
                            hideDivider
                        />
                        <MultiButtonQuestion
                            readonly={props.readonly}
                            questionConfig={getQuestionConfig(props.questionConfigs, questionIds.TinelsTestLeft)}
                            values={NORMAL_ABNORMAL_OPTIONS}
                        />
                        <p><b>{healthSurveillanceCrBugsEnabled ? <>Phalen's Test:</> : <>Phelan's Test:</>}</b></p>
                        <MultiButtonQuestion
                            readonly={props.readonly}
                            questionConfig={getQuestionConfig(props.questionConfigs, questionIds.PhelansTestRight)}
                            values={NORMAL_ABNORMAL_OPTIONS}
                            hideDivider
                        />
                        <MultiButtonQuestion
                            readonly={props.readonly}
                            questionConfig={getQuestionConfig(props.questionConfigs, questionIds.PhelansTestLeft)}
                            values={NORMAL_ABNORMAL_OPTIONS}
                        />
                    </>
                }
                <TextQuestion
                    readonly={props.readonly}
                    questionConfig={getQuestionConfig(props.questionConfigs, questionIds.AbnormalityOfNecksOrUpperLimbs)}
                    mandatory={isOldVersion}
                />
                <NumberQuestion
                    readonly={props.readonly}
                    questionConfig={getQuestionConfig(props.questionConfigs, questionIds.RightGripStrength1)}
                    mandatory={isOldVersion}
                    onAnswerChange={onUpdateRightGripStrength1}
                />
                <NumberQuestion
                    readonly={props.readonly}
                    questionConfig={getQuestionConfig(props.questionConfigs, questionIds.RightGripStrength2)}
                    mandatory={isOldVersion}
                    onAnswerChange={onUpdateRightGripStrength2}
                />
                <NumberQuestion
                    readonly={props.readonly}
                    questionConfig={getQuestionConfig(props.questionConfigs, questionIds.RightGripStrength3)}
                    mandatory={isOldVersion}
                    onAnswerChange={onUpdateRightGripStrength3}
                />
                <NumberQuestion
                    readonly
                    questionConfig={getQuestionConfig(props.questionConfigs, questionIds.RightGripStrengthAverage)}
                    mandatory={isOldVersion}
                />
                <NumberQuestion
                    readonly={props.readonly}
                    questionConfig={getQuestionConfig(props.questionConfigs, questionIds.LeftGripStrength1)}
                    mandatory={isOldVersion}
                    onAnswerChange={onUpdateLeftGripStrength1}
                />
                <NumberQuestion
                    readonly={props.readonly}
                    questionConfig={getQuestionConfig(props.questionConfigs, questionIds.LeftGripStrength2)}
                    mandatory={isOldVersion}
                    onAnswerChange={onUpdateLeftGripStrength2}
                />
                <NumberQuestion
                    readonly={props.readonly}
                    questionConfig={getQuestionConfig(props.questionConfigs, questionIds.LeftGripStrength3)}
                    mandatory={isOldVersion}
                    onAnswerChange={onUpdateLeftGripStrength3}
                />
                <NumberQuestion
                    readonly
                    questionConfig={getQuestionConfig(props.questionConfigs, questionIds.LeftGripStrengthAverage)}
                    mandatory={isOldVersion}
                />
                {!isOldVersion &&
                    <MultiButtonQuestion
                        readonly={props.readonly}
                        questionConfig={getQuestionConfig(props.questionConfigs, questionIds.AdvisoryLeafletReceivedByEmployee)}
                    />
                }
                {isOldVersion &&
                    <>
                        <MultiButtonQuestion
                            readonly={props.readonly}
                            questionConfig={getQuestionConfig(props.questionConfigs, questionIds.PrimaryRaynaudsPhenomenonPresent)}
                            mandatory
                        />
                        <MultiButtonQuestion
                            readonly={props.readonly}
                            questionConfig={getQuestionConfig(props.questionConfigs, questionIds.SecondaryRaynaudsPhenomenonPresent)}
                            mandatory
                        />
                        <MultiButtonQuestion
                            readonly={props.readonly}
                            questionConfig={getQuestionConfig(props.questionConfigs, questionIds.RaynaudsPhenomenonPresentVibrationInduced)}
                            mandatory
                        />
                        <DropdownQuestion
                            readonly={props.readonly}
                            questionConfig={getQuestionConfig(props.questionConfigs, questionIds.RightStockholmVascularGrading)}
                            mandatory
                            options={healthSurveillanceCrBugsEnabled ? stockholmVascularGradingOptions : stockholmSensorineuralGradingOptions}
                        />
                        <DropdownQuestion
                            readonly={props.readonly}
                            questionConfig={getQuestionConfig(props.questionConfigs, questionIds.LeftStockholmVascularGrading)}
                            mandatory
                            options={healthSurveillanceCrBugsEnabled ? stockholmVascularGradingOptions : stockholmSensorineuralGradingOptions}
                        />
                        <MultiButtonQuestion
                            readonly={props.readonly}
                            questionConfig={getQuestionConfig(props.questionConfigs, questionIds.NeurologicalImpairmentSuggested)}
                            mandatory
                        />
                        <MultiButtonQuestion
                            readonly={props.readonly}
                            questionConfig={getQuestionConfig(props.questionConfigs, questionIds.CarpalTunnelSyndromeSuggested)}
                            mandatory
                        />
                        <DropdownQuestion
                            readonly={props.readonly}
                            questionConfig={getQuestionConfig(props.questionConfigs, questionIds.RightStockholmSensorineuralGrading)}
                            mandatory
                            options={healthSurveillanceCrBugsEnabled ? stockholmSensorineuralGradingOptions : stockholmVascularGradingOptions}
                        />
                        <DropdownQuestion
                            readonly={props.readonly}
                            questionConfig={getQuestionConfig(props.questionConfigs, questionIds.LeftStockholmSensorineuralGrading)}
                            mandatory
                            options={healthSurveillanceCrBugsEnabled ? stockholmSensorineuralGradingOptions : stockholmVascularGradingOptions}
                        />
                        <MultiButtonQuestion
                            readonly={props.readonly}
                            questionConfig={getQuestionConfig(props.questionConfigs, questionIds.MuscularSoftTissueDisorderPresent)}
                            mandatory
                        />
                        <MultiButtonQuestion
                            readonly={props.readonly}
                            questionConfig={getQuestionConfig(props.questionConfigs, questionIds.EvidenceSkeletalDisorder)}
                            mandatory
                        />
                        <NumberQuestion
                            readonly={props.readonly}
                            questionConfig={getQuestionConfig(props.questionConfigs, questionIds.VascularLatentPeriods)}
                            mandatory
                        />
                        <NumberQuestion
                            readonly={props.readonly}
                            questionConfig={getQuestionConfig(props.questionConfigs, questionIds.NeurologicalLatentPeriods)}
                            mandatory
                        />
                        <NumberQuestion
                            readonly={props.readonly}
                            questionConfig={getQuestionConfig(props.questionConfigs, questionIds.MusculoskeletalLatentPeriods)}
                            mandatory
                        />
                        <MultiButtonQuestion
                            readonly={props.readonly}
                            questionConfig={getQuestionConfig(props.questionConfigs, questionIds.VibrotactileThresholdFurtherInvestigationsRequired)}
                            mandatory
                        />
                        <MultiButtonQuestion
                            readonly={props.readonly}
                            questionConfig={getQuestionConfig(props.questionConfigs, questionIds.TemperatureThresholdFurtherInvestigationsRequired)}
                            mandatory
                        />
                    </>
                }
            </>}
        </OldProgressBarQuestionnaireSectionUnconnected>
    );
};

// eslint-disable-next-line max-len
export const HealthSurveillanceConsultationExaminationSection = connect(makeMapStateToQuestionnaireSectionProps(sections.Examination.id), mapDispatchToQuestionnaireSectionProps)(HealthSurveillanceConsultationExaminationSectionUnconnected);
